import { useCallback, useEffect, useRef, useState } from 'react'

import { claimActiveAudio, createAudioInstanceId, releaseActiveAudio, subscribeActiveAudio } from './exclusiveAudioManager'

const LOAD_TIMEOUT_MS = 15_000

export interface UseAudioPlayerOptions {
    src: string
    isLoop: boolean
    preload: boolean
    showDuration: boolean
    onPlay?: () => void
}

export interface UseAudioPlayerResult {
    playing: boolean
    currentTime: number
    duration: number
    error: boolean
    play: () => void
    pause: () => void
    toggle: () => void
    seek: (time: number) => void
}

const resolvePreload = (preload: boolean, showDuration: boolean): HTMLMediaElement['preload'] => {
    if (preload) {
        return 'auto'
    }
    return showDuration ? 'metadata' : 'none'
}

export const useAudioPlayer = ({ src, isLoop, preload, showDuration, onPlay }: UseAudioPlayerOptions): UseAudioPlayerResult => {
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const instanceIdRef = useRef(createAudioInstanceId())
    const onPlayRef = useRef(onPlay)
    onPlayRef.current = onPlay
    const [playing, setPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [error, setError] = useState(false)

    useEffect(() => {
        const audio = new Audio()
        audio.volume = 1
        audio.preload = resolvePreload(preload, showDuration)
        audioRef.current = audio

        return () => {
            audio.pause()
            audio.removeAttribute('src')
            audio.load()
            releaseActiveAudio(instanceIdRef.current)
            audioRef.current = null
        }
        // 音频实例只创建一次，preload / showDuration 的变化走下面的独立 effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) {
            return
        }
        audio.loop = isLoop
    }, [isLoop])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) {
            return
        }
        audio.preload = resolvePreload(preload, showDuration)
    }, [preload, showDuration])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) {
            return
        }

        setPlaying(false)
        setCurrentTime(0)
        setDuration(0)
        setError(false)

        const syncDuration = () => {
            if (Number.isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration)
            }
        }
        const onTimeUpdate = () => {
            setCurrentTime(audio.currentTime)
        }
        const handlePlay = () => {
            claimActiveAudio(instanceIdRef.current)
            setPlaying(true)
            onPlayRef.current?.()
        }
        const onPause = () => {
            setPlaying(false)
        }
        const onEnded = () => {
            setPlaying(false)
            if (!audio.loop) {
                releaseActiveAudio(instanceIdRef.current)
            }
        }
        const onError = () => {
            setError(true)
            setPlaying(false)
        }

        audio.addEventListener('loadedmetadata', syncDuration)
        audio.addEventListener('durationchange', syncDuration)
        audio.addEventListener('timeupdate', onTimeUpdate)
        audio.addEventListener('play', handlePlay)
        audio.addEventListener('pause', onPause)
        audio.addEventListener('ended', onEnded)
        audio.addEventListener('error', onError)

        let timeoutId: ReturnType<typeof setTimeout> | undefined
        if (src) {
            audio.src = src
            audio.load()
            timeoutId = setTimeout(() => {
                if (audio.readyState < 1) {
                    setError(true)
                    setPlaying(false)
                }
            }, LOAD_TIMEOUT_MS)
        } else {
            audio.removeAttribute('src')
            audio.load()
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
            audio.pause()
            audio.removeEventListener('loadedmetadata', syncDuration)
            audio.removeEventListener('durationchange', syncDuration)
            audio.removeEventListener('timeupdate', onTimeUpdate)
            audio.removeEventListener('play', handlePlay)
            audio.removeEventListener('pause', onPause)
            audio.removeEventListener('ended', onEnded)
            audio.removeEventListener('error', onError)
        }
    }, [src])

    useEffect(() => {
        return subscribeActiveAudio(activeId => {
            const audio = audioRef.current
            if (!audio || activeId === instanceIdRef.current || audio.paused) {
                return
            }
            audio.pause()
        })
    }, [])

    const play = useCallback(() => {
        const audio = audioRef.current
        if (!audio || error || !src) {
            return
        }
        claimActiveAudio(instanceIdRef.current)
        void audio.play().catch(() => {
            setError(true)
            setPlaying(false)
        })
    }, [error, src])

    const pause = useCallback(() => {
        audioRef.current?.pause()
    }, [])

    const toggle = useCallback(() => {
        if (playing) {
            pause()
            return
        }
        play()
    }, [pause, play, playing])

    const seek = useCallback(
        (time: number) => {
            const audio = audioRef.current
            if (!audio || error) {
                return
            }
            const nextTime = Math.max(0, Number.isFinite(duration) && duration > 0 ? Math.min(time, duration) : time)
            audio.currentTime = nextTime
            setCurrentTime(nextTime)
        },
        [duration, error]
    )

    return {
        playing,
        currentTime,
        duration,
        error,
        play,
        pause,
        toggle,
        seek
    }
}
